import React from 'react';

/**
 * ProductsPage Component
 * Extracted from App.js for better code organization.
 * Handles products, addons, and packages tabs.
 */
const ProductsPage = ({
  // Data
  products,
  addons,
  packages,
  // State
  productsTab,
  compactView,
  productsViewType,
  showAddProduct,
  showAddAddon,
  showAddPackage,
  editingProduct,
  editingAddon,
  editingPackage,
  // Setters
  setProductsTab,
  setCompactView,
  setProductsViewType,
  setShowAddProduct,
  setShowAddAddon,
  setShowAddPackage,
  setEditingProduct,
  setEditingAddon,
  setEditingPackage,
  setActiveTab,
  setSettingsTab,
  // Functions
  hasPermission,
  deleteProduct,
  deleteAddon,
  deletePackage,
  openEditProduct,
  API
}) => {
  const safeProducts = Array.isArray(products) ? products : [];
  const safeAddons = Array.isArray(addons) ? addons : [];
  const safePackages = Array.isArray(packages) ? packages : [];

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Products & Services</h2>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex space-x-2">
          {[
            { key: 'products', label: 'Main Products' },
            { key: 'addons', label: 'Add-on Products' },
            { key: 'packages', label: 'Packages' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setProductsTab(tab.key)}
              className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                productsTab === tab.key ? 'bg-gray-100 font-semibold' : 'hover:bg-gray-50'
              }`}
              style={{
                color: productsTab === tab.key ? 'var(--stripe-primary)' : 'var(--stripe-text-secondary)'
              }}
              data-testid={`products-tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Compact View Toggle */}
        <button
          onClick={() => setCompactView(!compactView)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
            compactView ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          data-testid="compact-view-toggle"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {compactView ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            )}
          </svg>
          {compactView ? 'Compact' : 'Detailed'}
        </button>
      </div>

      {/* Products Tab Content */}
      {productsTab === 'products' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">Main Products</h3>
            <div className="flex items-center space-x-4">
              {/* View Toggle */}
              <div className="flex items-center bg-gray-600 rounded-lg p-1">
                <button
                  onClick={() => setProductsViewType('grid')}
                  className={`px-3 py-1 rounded text-sm transition-all ${
                    productsViewType === 'grid' 
                      ? 'bg-white text-gray-700 font-medium' 
                      : 'text-gray-300 hover:text-white'
                  }`}
                  data-testid="view-grid-btn"
                >
                  Grid
                </button>
                <button
                  onClick={() => setProductsViewType('list')}
                  className={`px-3 py-1 rounded text-sm transition-all ${
                    productsViewType === 'list' 
                      ? 'bg-white text-gray-700 font-medium' 
                      : 'text-gray-300 hover:text-white'
                  }`}
                  data-testid="view-list-btn"
                >
                  List
                </button>
              </div>
              {hasPermission('settings', 'manage_products') && (
                <button
                  onClick={() => setShowAddProduct(true)}
                  className="px-4 py-2 bg-white text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition"
                  data-testid="add-product-btn"
                >
                  + Add Product
                </button>
              )}
            </div>
          </div>
          
          {/* Products Grid/List View */}
          <div className={`p-6 ${productsViewType === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}`}>
            {safeProducts.length === 0 ? (
              <div className="col-span-full text-center py-8 text-gray-500">
                No products yet. Add your first product to get started.
              </div>
            ) : (
              safeProducts.map(product => (
                <div 
                  key={product.id} 
                  className={`bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition ${
                    productsViewType === 'list' ? 'flex items-center' : ''
                  }`}
                >
                  {/* Product Image */}
                  <div className={`${productsViewType === 'list' ? 'w-24 h-24 flex-shrink-0' : 'aspect-video'} bg-gray-100`}>
                    {product.image_url ? (
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : product.image_id || product.image_path ? (
                      <img 
                        src={`${API}/api/settings/products/${product.id}/image?url_only=false`}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-4xl">
                        📦
                      </div>
                    )}
                    {(product.image_id || product.image_path) && !product.image_url && (
                      <div className="w-full h-full hidden items-center justify-center text-gray-400 text-4xl">
                        📦
                      </div>
                    )}
                  </div>
                  
                  {/* Product Details */}
                  <div className={`p-4 ${productsViewType === 'list' ? 'flex-1 flex items-center justify-between' : ''}`}>
                    <div>
                      <h4 className="font-semibold text-gray-900">{product.name}</h4>
                      {!compactView && product.description && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{product.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2">
                        <span className="font-bold text-green-600">${product.price}</span>
                        {product.duration_minutes && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {product.duration_minutes} min
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {hasPermission('settings', 'manage_products') && (
                      <div className={`flex gap-2 ${productsViewType === 'list' ? '' : 'mt-4'}`}>
                        <button
                          onClick={() => openEditProduct(product)}
                          className="px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                          data-testid={`edit-product-${product.id}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteProduct(product.id)}
                          className="px-3 py-1 text-sm bg-red-50 text-red-600 rounded hover:bg-red-100"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Addons Tab Content */}
      {productsTab === 'addons' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">Add-on Products</h3>
            {hasPermission('settings', 'manage_products') && (
              <button
                onClick={() => {
                  setEditingAddon(null);
                  setShowAddAddon(true);
                }}
                className="px-4 py-2 bg-white text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition"
                data-testid="add-addon-btn"
              >
                + Add Add-on
              </button>
            )}
          </div>
          
          <div className="p-6">
            {safeAddons.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No add-ons yet. Add-ons can be selected with main products.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {safeAddons.map(addon => (
                  <div key={addon.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900">{addon.name}</h4>
                    {!compactView && addon.description && (
                      <p className="text-sm text-gray-500 mt-1">{addon.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      <span className="font-bold text-green-600">${addon.price}</span>
                      {hasPermission('settings', 'manage_products') && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setEditingAddon(addon);
                              setShowAddAddon(true);
                            }}
                            className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteAddon(addon.id)}
                            className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Packages Tab Content */}
      {productsTab === 'packages' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">Packages</h3>
            {hasPermission('settings', 'manage_products') && (
              <button
                onClick={() => {
                  setEditingPackage(null);
                  setShowAddPackage(true);
                }}
                className="px-4 py-2 bg-white text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition"
                data-testid="add-package-btn"
              >
                + Add Package
              </button>
            )}
          </div>
          
          <div className="p-6">
            {safePackages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No packages yet. Packages bundle multiple products together.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {safePackages.map(pkg => (
                  <div key={pkg.id} className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900">{pkg.name}</h4>
                    {!compactView && pkg.description && (
                      <p className="text-sm text-gray-600 mt-1">{pkg.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      <span className="font-bold text-green-600">${pkg.price}</span>
                      {hasPermission('settings', 'manage_products') && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setEditingPackage(pkg);
                              setShowAddPackage(true);
                            }}
                            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deletePackage(pkg.id)}
                            className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                    {pkg.product_ids?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <span className="text-xs text-gray-500">
                          {pkg.product_ids.length} product{pkg.product_ids.length !== 1 ? 's' : ''} included
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;
